import { confirmable } from 'react-confirm';
import StackedModal from './stacked_modal';
import { useTranslation } from 'react-i18next';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import {
  TextField,
  InlineStack,
  Text,
  BlockStack,
  IndexTable,
  Icon,
  Tooltip,
  useIndexResourceState,
} from '@shopify/polaris';
import { BarcodeIcon, ImageIcon } from '@shopify/polaris-icons';
import Image from 'src/components/image/image';
import { useProductName } from 'src/hooks/useProductName';
import { StockTypeBadge } from 'src/components/badge/stock_type_badge';
import { fNumber } from 'src/utils/format_number';
import { PRINT_CATEGORY, useGetPrintCatalog } from 'src/api/print_center_api';
import { LABEL_PRINT_TYPE } from 'src/sections/printing/label_print_payload';
import FormProvider from 'src/components/hook_form/form_provider';
import { RHFAPIAutocompleteV2 } from 'src/components/hook_form/rhf_api_autocomplete_v2';
import { isEmpty } from 'src/utils/type_check';
import i18n from 'src/locales/i18n';

i18n.loadNamespaces(['printing', 'placeholder']);

export const PRINT_LABEL_TYPE = {
  BARCODE_ONLY: 'barcode_only',
  BARCODE_AND_PRODUCT_INFO: 'barcode_and_product_info',
};

const PRINT_TYPE_OF = {
  [PRINT_LABEL_TYPE.BARCODE_ONLY]: LABEL_PRINT_TYPE.SIMPLE,
  [PRINT_LABEL_TYPE.BARCODE_AND_PRODUCT_INFO]: LABEL_PRINT_TYPE.PRODUCT,
};

const SIZE_FIELD_WIDTH = '13.75rem';

const PRODUCT_NAME_MAX_WIDTH = { xs: '13rem', sm: '18rem', md: '26rem' };

const sizeLabel = (size) => size.name ?? `${size.widthMm} × ${size.heightMm} mm`;

function OptionCard({ selected, image, label, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        flex: 1,
        cursor: 'pointer',
        padding: '12px',
        borderRadius: 'var(--p-border-radius-300)',
        border: `var(--p-border-width-050) solid ${
          selected ? 'var(--p-color-border-emphasis)' : 'var(--p-color-border)'
        }`,
        boxShadow: selected ? '0 0 0 1px var(--p-color-border-emphasis)' : 'none',
        backgroundColor: selected
          ? 'var(--p-color-bg-surface-selected)'
          : 'var(--p-color-bg-surface)',
      }}
    >
      <InlineStack
        gap="300"
        blockAlign="center"
        wrap={false}
      >
        <Box
          component="img"
          src={image}
          alt={label}
          sx={{ width: 40, height: 40, flexShrink: 0, objectFit: 'contain' }}
        />
        <Text
          variant="bodyMd"
          as="span"
        >
          {label}
        </Text>
      </InlineStack>
    </Box>
  );
}

function LabelProductCell({ row, showStockType }) {
  const productName = useProductName(row.productNameLocal, row.productNameEnglish);
  return (
    <InlineStack
      align="space-between"
      blockAlign="center"
      wrap={false}
      gap="300"
    >
      <InlineStack
        gap="300"
        blockAlign="center"
        wrap={false}
      >
        {row.representativeImageUrl ? (
          <Image
            src={row.representativeImageUrl}
            alt={row.sku}
            sx={{
              width: 40,
              height: 40,
              borderRadius: 'var(--p-border-radius-200)',
              flexShrink: 0,
            }}
          />
        ) : (
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 'var(--p-border-radius-200)',
              border: 'var(--p-border-width-025) solid var(--p-color-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ width: 20, height: 20 }}>
              <Icon
                source={ImageIcon}
                tone="subdued"
              />
            </Box>
          </Box>
        )}
        <BlockStack gap="050">
          <Tooltip
            content={productName}
            preferredPosition="above"
          >
            <Box
              sx={{
                maxWidth: PRODUCT_NAME_MAX_WIDTH,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <Text
                variant="bodyMd"
                fontWeight="medium"
                as="span"
              >
                {productName}
              </Text>
            </Box>
          </Tooltip>
          <Text
            variant="bodyMd"
            as="span"
          >
            {`SKU: ${row.sku}`}
          </Text>
          {row.barcode && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexShrink: 0,
                  '& .Polaris-Icon': { width: 16, height: 16, margin: 0 },
                }}
              >
                <Icon source={BarcodeIcon} />
              </Box>
              <Text
                variant="bodyMd"
                as="span"
              >
                {row.barcode}
              </Text>
            </Box>
          )}
        </BlockStack>
      </InlineStack>
      {showStockType && row.stockType && (
        <Box sx={{ flexShrink: 0 }}>
          <StockTypeBadge stockType={row.stockType} />
        </Box>
      )}
    </InlineStack>
  );
}

export function LabelSizeField({ card, value, onChange }) {
  const { t } = useTranslation(['printing', 'placeholder']);
  const sizes = useMemo(() => card?.sizes ?? [], [card]);
  const useSizeOptions = useCallback(() => ({ searchResults: sizes }), [sizes]);
  const sizeMethods = useForm({ defaultValues: { labelSize: value } });
  const selectedSize = sizes.find((size) => size.code === value);

  useEffect(() => {
    sizeMethods.reset({ labelSize: value });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const notSelected = isEmpty(card);
  const staticText =
    sizes.length === 1
      ? sizeLabel(sizes[0])
      : card?.widthMm && card?.heightMm
        ? `${card.widthMm} × ${card.heightMm} mm`
        : t('printing:a4');

  return (
    <InlineStack
      gap="300"
      blockAlign="center"
      wrap={false}
    >
      <Text
        variant="bodyMd"
        as="span"
      >
        {t('printing:size')}
      </Text>
      {notSelected || sizes.length > 1 ? (
        <Box sx={{ width: SIZE_FIELD_WIDTH }}>
          <FormProvider methods={sizeMethods}>
            <RHFAPIAutocompleteV2
              name="labelSize"
              useOptions={useSizeOptions}
              toOption={(size) => ({ label: sizeLabel(size), value: size.code })}
              onChangeWithValue
              onChange={(option) => onChange(option?.value ?? '')}
              initialText={selectedSize ? sizeLabel(selectedSize) : ''}
              disabled={notSelected}
              placeholder={
                notSelected ? t('printing:message.select_label_first') : t('placeholder:size')
              }
              hideSearchIcon
              resetSearchOnOpen
            />
          </FormProvider>
        </Box>
      ) : (
        <Text
          variant="bodyMd"
          as="span"
        >
          {staticText}
        </Text>
      )}
    </InlineStack>
  );
}

function PrintLabelTypeDialog({
  show,
  proceed,
  title,
  items = [],
  selectable = true,
  showOrdered = true,
  showStockType = true,
  defaultToOrdered = false,
  withSize = false,
  getRowId,
}) {
  const { t } = useTranslation(['common', 'printing']);
  const [type, setType] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [labelSize, setLabelSize] = useState('');
  const { searchResults: catalog } = useGetPrintCatalog(PRINT_CATEGORY.LABEL);

  const sizeCard = useMemo(
    () => (withSize ? catalog.find((item) => item.code === PRINT_TYPE_OF[type]) ?? null : null),
    [withSize, catalog, type],
  );

  useEffect(() => {
    setLabelSize((sizeCard?.sizes ?? []).find((size) => size.isDefault)?.code ?? '');
  }, [sizeCard]);

  const rows = useMemo(
    () =>
      items.map((item, index) => ({
        ...item,
        id: String(getRowId ? getRowId(item) : item.purchaseOrderLine ?? index),
      })),
    [items, getRowId],
  );

  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(rows);

  const isRowSelected = (id) => !selectable || selectedResources.includes(id);

  const getDefaultQuantity = (row) =>
    String(defaultToOrdered && row.productQuantity != null ? row.productQuantity : 1);

  const getQuantity = (row) => quantities[row.id] ?? getDefaultQuantity(row);

  const formatQuantity = (value) =>
    value === '' || value == null ? '' : Number(value).toLocaleString('en-US');

  const handleQuantityChange = (id, newValue) => {
    const numbersOnly = newValue.replace(/[^0-9]/g, '');
    setQuantities((prev) => ({
      ...prev,
      [id]: numbersOnly === '' ? '' : String(parseInt(numbersOnly, 10)),
    }));
  };

  const rowMarkup = rows.map((row, index) => (
    <IndexTable.Row
      id={row.id}
      key={row.id}
      selected={selectedResources.includes(row.id)}
      position={index}
      onClick={() => {}}
    >
      <IndexTable.Cell>
        <LabelProductCell
          row={row}
          showStockType={showStockType}
        />
      </IndexTable.Cell>
      {showOrdered && (
        <IndexTable.Cell>
          <Box sx={{ textAlign: 'right' }}>{fNumber(row.productQuantity)}</Box>
        </IndexTable.Cell>
      )}
      <td
        onClick={(e) => e.stopPropagation()}
        style={{ width: '160px', padding: 'var(--p-space-150)' }}
      >
        <TextField
          labelHidden
          label={t('printing:number_of_labels')}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          align="right"
          selectTextOnFocus
          value={formatQuantity(getQuantity(row))}
          disabled={!isRowSelected(row.id)}
          onChange={(value) => handleQuantityChange(row.id, value)}
        />
      </td>
    </IndexTable.Row>
  ));

  return (
    <StackedModal
      isOpen={show}
      title={title || t('common:button.print_label')}
      content={
        <BlockStack gap="400">
          <InlineStack
            gap="400"
            wrap={false}
          >
            <OptionCard
              selected={type === PRINT_LABEL_TYPE.BARCODE_ONLY}
              image="/images/barcode.png"
              label={t('printing:barcode_only')}
              onClick={() => setType(PRINT_LABEL_TYPE.BARCODE_ONLY)}
            />
            <OptionCard
              selected={type === PRINT_LABEL_TYPE.BARCODE_AND_PRODUCT_INFO}
              image="/images/barcode_product.png"
              label={t('printing:barcode_and_product_info')}
              onClick={() => setType(PRINT_LABEL_TYPE.BARCODE_AND_PRODUCT_INFO)}
            />
          </InlineStack>

          <Box
            sx={{
              border: 'var(--p-border-width-025) solid var(--p-color-border)',
              borderRadius: 'var(--p-border-radius-300)',
              overflow: 'hidden',
              maxHeight: '360px',
              overflowY: 'auto',
            }}
          >
            <IndexTable
              selectable={selectable}
              resourceName={{
                singular: t('common:field.product'),
                plural: t('common:field.product'),
              }}
              itemCount={rows.length}
              selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
              onSelectionChange={handleSelectionChange}
              headings={[
                { title: t('common:field.product') },
                ...(showOrdered ? [{ title: t('printing:ordered_qty'), alignment: 'end' }] : []),
                { title: t('printing:number_of_labels'), alignment: 'end' },
              ]}
            >
              {rowMarkup}
            </IndexTable>
          </Box>
        </BlockStack>
      }
      footerLeftNode={
        withSize ? (
          <LabelSizeField
            card={sizeCard}
            value={labelSize}
            onChange={setLabelSize}
          />
        ) : null
      }
      primaryAction={{
        content: t('common:button.print_label'),
        disabled: !type || (selectable && selectedResources.length === 0),
        onAction: () => {
          const selectedItems = rows
            .filter((row) => isRowSelected(row.id))
            .map((row) => ({
              ...row,
              numberOfLabels: parseInt(getQuantity(row), 10) || 1,
            }));
          proceed({ confirmed: true, type, labelSize, items: selectedItems });
        },
      }}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        proceed({ confirmed: false, type: null, labelSize: '', items: [] });
      }}
    />
  );
}

export default confirmable(PrintLabelTypeDialog);
